import type { AppRouter } from "@payrollmart-invoice/api/trpc";
import { QueryClient } from "@tanstack/react-query";
import { TRPCClientError, httpBatchLink } from "@trpc/client";
import { createTRPCReact } from "@trpc/react-query";
import superjson from "superjson";

export const trpc = createTRPCReact<AppRouter>();

function isUnauthorizedError(error: unknown): boolean {
	return error instanceof TRPCClientError && error.data?.code === "UNAUTHORIZED";
}

/**
 * Backend returns FORBIDDEN with message "2FA_REQUIRED" when a session is
 * authenticated but the user hasn't enrolled 2FA yet. Redirect to
 * /onboarding so they can finish the wizard — retrying the same request
 * won't help, and a raw FORBIDDEN toast is confusing.
 */
function isTwoFactorRequiredError(error: unknown): boolean {
	return (
		error instanceof TRPCClientError &&
		error.data?.code === "FORBIDDEN" &&
		error.message === "2FA_REQUIRED"
	);
}

export const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			staleTime: 30_000,
			retry: (failureCount, error) => {
				if (isUnauthorizedError(error) || isTwoFactorRequiredError(error)) return false;
				return failureCount < 1;
			},
		},
		mutations: {
			onError: (error) => {
				if (isUnauthorizedError(error)) {
					window.location.href = "/login";
				} else if (isTwoFactorRequiredError(error)) {
					window.location.href = "/onboarding";
				}
			},
		},
	},
});

queryClient.getQueryCache().subscribe((event) => {
	if (event.type === "updated" && event.action.type === "error") {
		if (isUnauthorizedError(event.action.error)) {
			window.location.href = "/login";
		} else if (isTwoFactorRequiredError(event.action.error)) {
			window.location.href = "/onboarding";
		}
	}
});

// Relative URL — nginx proxies /api/ to the API server.
// Same-origin requests, no cross-origin cookie issues.
export const trpcClient = trpc.createClient({
	links: [
		httpBatchLink({
			url: "/api/trpc",
			transformer: superjson,
		}),
	],
});
